import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import './Pages.css';

const monthLabel = (month) => {
  const [year, m] = month.split('-');
  return new Date(Number(year), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
};

const PractitionerReports = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;
  const userId = user?.id || user?.uuid || JSON.parse(localStorage.getItem('user') || '{}')?.uuid;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${apiUrl}/practitioners/${userId}/reports/summary`, { headers });
        setReport(response.data);
      } catch (err) {
        setError('Failed to load reports');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, navigate]);

  if (loading) return <div className="page-container">Loading reports...</div>;
  if (error) return <div className="page-container"><div className="error-message">{error}</div></div>;

  const revenueData = (report.revenue_by_month || []).map((r) => ({ ...r, label: monthLabel(r.month), total: parseFloat(r.total) }));
  const totalRevenue = revenueData.reduce((sum, r) => sum + r.total, 0);
  const totalBookings = (report.bookings_by_status || []).reduce((sum, b) => sum + parseInt(b.count, 10), 0);
  const totalNewClients = (report.new_clients_by_month || []).reduce((sum, c) => sum + parseInt(c.new_clients, 10), 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/practitioner/dashboard')}>Back to Dashboard</button>
      </div>

      <div className="flex-between" style={{ background: '#f5f6fa', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1.2rem', color: '#16a34a' }}>${totalRevenue.toFixed(2)}</div>
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>Revenue (12 mo)</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>{totalBookings}</div>
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>Total Bookings</div>
        </div>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1.2rem' }}>{totalNewClients}</div>
          <div className="text-muted" style={{ fontSize: '0.75rem' }}>New Clients (12 mo)</div>
        </div>
      </div>

      <div className="card-header">Revenue by Month</div>
      <div className="list-container" style={{ padding: '16px 8px 8px', marginBottom: 20 }}>
        {revenueData.length === 0 ? (
          <p className="empty-state">No paid invoices yet</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Revenue']} />
              <Bar dataKey="total" fill="#c2366f" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="card-header">Bookings by Status</div>
      <div className="list-container" style={{ padding: 14, marginBottom: 20 }}>
        {(report.bookings_by_status || []).length === 0 ? (
          <p className="empty-state">No bookings yet</p>
        ) : (
          (report.bookings_by_status || []).map((b) => (
            <span key={b.status} className={`badge badge-${b.status}`} style={{ marginRight: 8, marginBottom: 8, display: 'inline-block' }}>
              {b.status}: {b.count}
            </span>
          ))
        )}
      </div>

      <div className="card-header">Top Services by Revenue</div>
      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Service</th><th>Revenue</th></tr>
          </thead>
          <tbody>
            {(report.top_services || []).map((s) => (
              <tr key={s.name}>
                <td>{s.name}</td>
                <td>${parseFloat(s.revenue).toFixed(2)}</td>
              </tr>
            ))}
            {(report.top_services || []).length === 0 && <tr><td colSpan="2" className="empty-state">No paid invoices yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PractitionerReports;
